/**
 * LegalPageShell — shared chrome for /privacy, /terms, /refund.
 *
 * Public route, no auth gate. Keeps the legal surfaces uniform,
 * printable, and mobile-friendly. The same shell is mounted both as
 * a full page (`/privacy`, `/terms`, `/refund`) and inside the
 * `AccountPage > Privacy` tab (where it renders without its own
 * chrome via the `embedded` flag).
 *
 * RC 1.35.F1 P0-2 / P0-3 / P0-4 — legal pages.
 */
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, ShieldCheck, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';

const NAV_ITEMS = [
  { to: '/terms',   label: 'Terms of Service', icon: Scale,        testid: 'legal-nav-terms' },
  { to: '/privacy', label: 'Privacy Policy',   icon: ShieldCheck,  testid: 'legal-nav-privacy' },
  { to: '/refund',  label: 'Refund Policy',    icon: FileText,     testid: 'legal-nav-refund' },
];

export default function LegalPageShell({ title, updatedAt, activePath, children, embedded = false, testid }) {
  const content = (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid={testid}>
      {!embedded && (
        <div className="mb-6">
          <Button asChild variant="ghost" size="sm" className="text-ndor-ink-muted hover:text-ndor-ink -ml-2">
            <Link to="/" data-testid="legal-back-home">
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Back to NDORFlow
            </Link>
          </Button>
        </div>
      )}

      <header className="space-y-2 mb-8 pb-6 border-b border-ndor-edge">
        <p className="text-[11px] uppercase tracking-[0.22em] text-ndor-gold font-mono-ui">
          NDORFlow Legal
        </p>
        <h1 className="text-3xl sm:text-4xl font-serif-display font-semibold tracking-tight text-ndor-ink">
          {title}
        </h1>
        <p className="text-xs text-ndor-ink-muted font-mono-ui" data-testid="legal-updated-at">
          Last updated {updatedAt}
        </p>
      </header>

      <nav className="flex flex-wrap gap-2 mb-10" data-testid="legal-page-nav">
        {NAV_ITEMS.map(({ to, label, icon: Icon, testid: tid }) => {
          const active = activePath === to;
          return (
            <Link
              key={to}
              to={to}
              data-testid={tid}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-colors ${
                active
                  ? 'bg-ndor-ink text-ndor-canvas border-ndor-ink'
                  : 'bg-transparent text-ndor-ink-muted border-ndor-edge hover:bg-ndor-surface-alt/50'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </Link>
          );
        })}
      </nav>

      <article
        className="prose prose-sm max-w-none text-ndor-ink-secondary font-manrope space-y-6
                   prose-headings:font-serif-display prose-headings:text-ndor-ink prose-headings:font-semibold prose-headings:tracking-tight
                   prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-3
                   prose-h3:text-base prose-h3:mt-6 prose-h3:mb-2
                   prose-p:text-sm prose-p:leading-relaxed
                   prose-li:text-sm prose-li:leading-relaxed
                   prose-strong:text-ndor-ink prose-strong:font-semibold
                   prose-a:text-ndor-ink prose-a:underline hover:prose-a:text-ndor-ink/80"
        data-testid="legal-page-body"
      >
        {children}
      </article>

      <footer className="mt-12 pt-6 border-t border-ndor-edge text-xs text-ndor-ink-muted space-y-1">
        <p>
          Questions about this document? Contact{' '}
          <a href="mailto:support@ndor.app" data-testid="legal-contact-email">support@ndor.app</a>.
        </p>
        <p data-testid="legal-shell-copyright">
          © {new Date().getFullYear()} NDOR Labs Limited. All rights reserved.
        </p>
        <p className="leading-relaxed">
          NDORFlow is developed and owned by NDOR Labs Limited. Commercial
          services, subscriptions and customer support are provided by
          IAA Energy Resources Limited.
        </p>
      </footer>
    </div>
  );

  if (embedded) return content;

  return (
    <div className="min-h-screen bg-ndor-canvas">
      {content}
    </div>
  );
}
